import { useParams } from 'react-router-dom';
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faChartLine,
  faTools, faEye, faPrint, faFileAlt, faUser, faCalendarAlt, faCheckCircle, faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { useGetOccurrenceByIdQuery } from '../../redux/apiSlice';
import Indentification from '../components/SrmForms/HazardIdentification/Indentification';
import Analysis from '../components/SrmForms/RiskAnalysis/Analysis';
import Mitigation from '../components/SrmForms/RiskMitigation/Mitigation';
import Monitoring from '../components/SrmForms/HazardMitigationMonitoring/Monitoring';

function SrmForms() {
  const { id } = useParams();
  const [breadCrumb, setBreadCrumb] = useState('Hazard Identification');

  // Use RTK Query to fetch occurrence data
  const {
    data: srm,
    isLoading: loading,
    error: errors,
  } = useGetOccurrenceByIdQuery(id, {
    skip: !id,
  });

  const pages = [
    {
      name: 'Hazard Identification',
      icon: faExclamationTriangle,
      href: '#',
      current: breadCrumb === 'Hazard Identification',
      description: 'Identify and document safety hazards',
    },
    {
      name: 'Risk Analysis',
      icon: faChartLine,
      href: '#',
      current: breadCrumb === 'Risk Analysis',
      description: 'Analyze risks and assess impact',
    },
    {
      name: 'Risk Mitigation Treatment',
      icon: faTools,
      href: '#',
      current: breadCrumb === 'Risk Mitigation Treatment',
      description: 'Develop mitigation strategies',
    },
    {
      name: 'Hazard Mitigation Monitoring',
      icon: faEye,
      href: '#',
      current: breadCrumb === 'Hazard Mitigation Monitoring',
      description: 'Monitor and track progress',
    },
  ];

  // Helper function to render content based on loading/error states
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4" />
            <p className="text-gray-300">Loading safety data...</p>
          </div>
        </div>
      );
    }

    if (errors) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl text-red-400 mb-4" />
            <p className="text-gray-300">Error loading safety data</p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gray-750 rounded-lg p-6 shadow-lg">
        {/* Case Summary */}
        {srm && (
        <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-600">
          <h3 className="text-lg font-semibold text-white mb-3">Case Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-gray-400 text-sm">Incident:</span>
              <span className="text-white font-medium">{srm.incident_summary || 'No summary available'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-400 text-sm">Category:</span>
              <span className="text-white font-medium">{srm.category || 'Not specified'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-400 text-sm">Department:</span>
              <span className="text-white font-medium">{srm.department || 'Not specified'}</span>
            </div>
          </div>
        </div>
        )}

        {/* SRM Content */}
        <div className="bg-gray-800 rounded-lg border border-gray-600">
          {breadCrumb === 'Hazard Identification' && (
          <Indentification srm={srm} />
          )}
          {breadCrumb === 'Risk Analysis' && <Analysis srm={srm} />}
          {breadCrumb === 'Risk Mitigation Treatment' && (
          <Mitigation srm={srm} />
          )}
          {breadCrumb === 'Hazard Mitigation Monitoring' && (
          <Monitoring srm={srm} />
          )}
        </div>
      </div>
    );
  };

  // Helper function to get progress circle class
  const getProgressCircleClass = (pageName, index) => {
    if (breadCrumb === pageName) return 'bg-red-600 text-white';
    if (index < pages.findIndex((p) => p.name === breadCrumb)) return 'bg-green-600 text-white';
    return 'bg-gray-600 text-gray-400';
  };

  const handleCrumb = (e) => {
    const selectedCrumb = e.target.id;
    setBreadCrumb(selectedCrumb);
  };

  const print124 = (e) => e.preventDefault();
  const printHazardIdentification = (e) => e.preventDefault();
  const printSITREP = (e) => e.preventDefault();

  return (
    <div className="p-6 w-full min-h-screen bg-gray-900">
      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1 bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
          {/* Header Section */}
          <div className="bg-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-600 rounded-lg">
                  <FontAwesomeIcon icon={faShieldAlt} className="text-white text-lg" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-white">Safety Risk Management</h1>
                  <p className="text-gray-300 text-sm">Comprehensive safety analysis and risk assessment</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-300">
                <FontAwesomeIcon icon={faUser} className="mr-1" />
                <span>
                  Case ID:
                  {id}
                </span>
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 bg-gray-750 border-b border-gray-600">
            <div className="flex items-center justify-between">
              {pages.map((page, index) => (
                <div key={page.name} className="flex items-center">
                  <button
                    onClick={handleCrumb}
                    id={page.name}
                    type="button"
                    className={`flex items-center space-x-3 px-6 py-3 rounded-lg transition-all duration-200 ${
                      breadCrumb === page.name
                        ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg transform scale-105'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                    }`}
                  >
                    <div className={`p-2 rounded-full ${
                      breadCrumb === page.name ? 'bg-white/20' : 'bg-gray-600'
                    }`}
                    >
                      <FontAwesomeIcon icon={page.icon} className="text-sm" />
                    </div>
                    <span className="text-sm font-medium">{page.name}</span>
                  </button>
                  {index < pages.length - 1 && (
                    <div className="w-12 h-0.5 bg-gray-600 mx-4" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {renderContent()}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-80 bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
          {/* Case Information */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FontAwesomeIcon icon={faFileAlt} className="mr-2 text-red-400" />
              Case Information
            </h3>
            <div className="space-y-4">
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Case ID</span>
                  <span className="text-white font-semibold">{id}</span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Status</span>
                  <span className="text-yellow-400 font-semibold">{srm?.status || 'In Progress'}</span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Current Step</span>
                  <span className="text-red-400 font-semibold">{breadCrumb}</span>
                </div>
              </div>
              {srm?.number && (
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Number</span>
                  <span className="text-blue-400 font-semibold">{srm.number}</span>
                </div>
              </div>
              )}
              {srm?.place && (
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Place</span>
                  <span className="text-green-400 font-semibold">{srm.place}</span>
                </div>
              </div>
              )}
              {srm?.reported_by && (
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Reported By</span>
                  <span className="text-purple-400 font-semibold">{srm.reported_by}</span>
                </div>
              </div>
              )}
            </div>
          </div>

          {/* Progress Tracking */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Progress Tracking</h3>
            <div className="space-y-3">
              {pages.map((page, index) => (
                <div key={page.name} className="bg-gray-750 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        getProgressCircleClass(page.name, index)
                      }`}
                      >
                        {index < pages.findIndex((p) => p.name === breadCrumb) ? (
                          <FontAwesomeIcon icon={faCheckCircle} className="text-xs" />
                        ) : (
                          <span className="text-xs">{index + 1}</span>
                        )}
                      </div>
                      <span className={`text-sm ${
                        breadCrumb === page.name ? 'text-white font-medium' : 'text-gray-300'
                      }`}
                      >
                        {page.name}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                type="button"
                onClick={print124}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2"
              >
                <FontAwesomeIcon icon={faPrint} className="text-sm" />
                <span>SMS Form 124</span>
              </button>
              <button
                type="button"
                onClick={printHazardIdentification}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2"
              >
                <FontAwesomeIcon icon={faFileAlt} className="text-sm" />
                <span>Hazard ID Report</span>
              </button>
              <button
                type="button"
                onClick={printSITREP}
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2"
              >
                <FontAwesomeIcon icon={faFileAlt} className="text-sm" />
                <span>SITREP</span>
              </button>
            </div>
          </div>

          {/* Data Summary */}
          {srm && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FontAwesomeIcon icon={faChartLine} className="mr-2 text-red-400" />
              Data Summary
            </h3>
            <div className="bg-gray-750 rounded-lg p-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full" />
                    <span className="text-gray-300 text-sm">Safety Recommendations</span>
                  </div>
                  <span className="text-white font-semibold">{srm.safety_recommendation_status?.length || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full" />
                    <span className="text-gray-300 text-sm">Actions Taken</span>
                  </div>
                  <span className="text-white font-semibold">{srm.actions_taken?.length || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                    <span className="text-gray-300 text-sm">Causes Identified</span>
                  </div>
                  <span className="text-white font-semibold">{srm.causes?.length || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full" />
                    <span className="text-gray-300 text-sm">System States</span>
                  </div>
                  <span className="text-white font-semibold">{srm.system_states?.length || 0}</span>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Assignment Info */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Assignment</h3>
            <div className="bg-gray-750 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                <span className="text-gray-300 text-sm">Assigned To</span>
              </div>
              <p className="text-white font-medium">Rogers Wanzumula</p>
              <div className="flex items-center space-x-2 mt-3">
                <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400" />
                <span className="text-gray-300 text-sm">Started: Today</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SrmForms;
